import isEqual from 'lodash/isEqual';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';

import type { SalesforceSubmission } from './buildSalesforceSubmission';

export type RecordKind = Exclude<keyof SalesforceSubmission, 'opportunity'>;

export type ChangeStatus = 'added' | 'changed' | 'deleted' | 'unchanged';

export type Row = Record<string, string | number | boolean | null>;

type SubmittedRecord = SalesforceSubmission[RecordKind][number];

export interface RecordChange {
  status: ChangeStatus;
  row: Row;
  previousRow?: Row;
  changedColumns: Array<string>;
}

type Names = Map<string, string>;

// svc-offer never deletes an asset a submission drops.
const NEVER_DELETED: Array<keyof SalesforceSubmission> = ['room_assets', 'inclusion_assets'];

export const refNames = (submission: SalesforceSubmission): Names =>
  new Map(
    [...submission.room_assets, ...submission.inclusion_assets, ...submission.deal_offers].map(({ ref, fields }) => [
      ref,
      String(fields.name),
    ]),
  );

function flattenRecord({ ref: _ref, fields, ...links }: SubmittedRecord, names: Names): Row {
  const linkColumns = Object.fromEntries(
    Object.entries(links).map(([column, value]) => [
      column,
      typeof value === 'string' ? (names.get(value) ?? value) : null,
    ]),
  );
  return { ...linkColumns, ...fields };
}

// Matches how svc-offer keys records: deal option children are scoped by their deal option.
const recordKey = (record: SubmittedRecord) =>
  'deal_offer' in record ? `${record.deal_offer}::${record.ref}` : record.ref;

// Only columns this submission sends: one it leaves out keeps its Salesforce value, so it cannot change.
const changedColumns = (row: Row, previousRow: Row) =>
  Object.keys(row).filter((column) => !isEqual(row[column] ?? null, previousRow[column] ?? null));

export function diffRecords(
  kind: keyof SalesforceSubmission,
  records: Array<SubmittedRecord>,
  previous: Array<SubmittedRecord> | undefined,
  names: { current: Names; previous: Names } = { current: new Map(), previous: new Map() },
): Array<RecordChange> {
  const added = (record: SubmittedRecord): RecordChange => ({
    status: 'added',
    row: flattenRecord(record, names.current),
    changedColumns: [],
  });
  if (!previous) {
    return records.map(added);
  }
  const previousByKey = keyBy(previous, recordKey);
  const submittedKeys = new Set(records.map(recordKey));

  const submitted = records.map((record): RecordChange => {
    const before = previousByKey[recordKey(record)];
    if (!before) {
      return added(record);
    }
    const row = flattenRecord(record, names.current);
    const previousRow = flattenRecord(before, names.previous);
    const changed = changedColumns(row, previousRow);
    return { status: changed.length ? 'changed' : 'unchanged', row, previousRow, changedColumns: changed };
  });
  const deleted = NEVER_DELETED.includes(kind)
    ? []
    : previous
        .filter((record) => !submittedKeys.has(recordKey(record)))
        .map(
          (record): RecordChange => ({
            status: 'deleted',
            row: flattenRecord(record, names.previous),
            changedColumns: [],
          }),
        );

  return [...submitted, ...deleted];
}

function diffOpportunity(
  opportunity: SalesforceSubmission['opportunity'],
  previous: SalesforceSubmission['opportunity'] | undefined,
): Array<RecordChange> {
  if (Object.keys(opportunity).length === 0) {
    return [];
  }
  const record = { ref: 'opportunity', fields: opportunity };
  const before = { ref: 'opportunity', fields: pick(previous ?? {}, Object.keys(opportunity)) };
  return diffRecords('opportunity', [record], [before]);
}

export function diffSubmission(submission: SalesforceSubmission, previous: SalesforceSubmission | undefined) {
  const names = { current: refNames(submission), previous: previous ? refNames(previous) : new Map() };
  const kinds = Object.keys(submission).filter((kind): kind is RecordKind => kind !== 'opportunity');
  return {
    opportunity: diffOpportunity(submission.opportunity, previous?.opportunity),
    records: Object.fromEntries(
      kinds.map((kind) => [kind, diffRecords(kind, submission[kind], previous && (previous[kind] ?? []), names)]),
    ) as Record<RecordKind, Array<RecordChange>>,
  };
}
